import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Command } from 'cmdk';
import {
  ClearDiv,
  EmptyStateDiv,
  IconDiv,
  ItemDiv,
  Main,
  MainDiv,
  Mark,
  PopUp,
  Profile,
  ProjectFilterChip,
  ProjectFilterHint,
  ProjectName,
  SearchArea,
  Tab,
  Text,
  WorkspaceSwitch
} from './vercelstyle';
import { useHistory } from 'react-router-dom';
import { isElectronApp, isEmpty, isMediaRecordingSupported, renderStatusIconName } from '../../helpers/common';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import {
  AVATAR_BORDER,
  DESKTOP_VIEW,
  EMPTY_STATE_SIZE,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  ICON_COLORS,
  ICON_SIZE,
  PROJECT_ICON_TYPE,
  STATUS_TYPE,
  TAB_BUTTON_TYPE,
  TABS_COMMAND_K,
  TABS_COMMAND_K_ID
} from '../../global/constants';
import Avatar from '../avatar';
import RecordItem from './recordItem';
import CmdItem from './cmdkItem';
import { useMobileDevice } from '../../global/useMobile';
import UserPreferenceSingleton from '../../helpers/userPreferenceSingleton';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { UserModel } from '../../mobx/models/user';
import { ProjectModel } from '../../mobx/models/project';
import isArray from 'lodash/isArray';
import { convertQueryToWordsCollection, SearchResultItem } from '../../services/universalSearch';
import { TaskModel } from '../../mobx/models/task';
import useKeyboardVisibility from '../../global/useKeyboard';
import ProjectIconAvatar from '../projectIconAvatar';
import Tabs from '../tabs';
import IconButton from '../button/iconButton';
import { ROUTES } from '../../routes/urls';
import EmptyStateV2 from '../emptyState';
import ModalHeader from '../modals/modalHeader';
import { CustomerDetailsInterface } from '../../interfaces/CustomerInterface';
import Image from '../image';

declare global {
  interface Window {
    Intercom?: (command: string) => void;
  }
}

const QUICK_ACTION_LIST = [
  'Create new task',
  'Create new project',
  'Snap',
  'Record Snap',
  'Navigating to setting',
  'Recurring Task',
  'Views',
  ...(isElectronApp() ? ['Quick workspace switch'] : [])
];

const HELP_SECTION_LIST = ['Search Docs', 'Send Feedback', 'Contact Support'];

export const VercelCMDK = observer(function VercelCMDK({
  closePopup,
  openTaskModal,
  openSnapModal,
  openUserModal,
  isSearchScreen,
  initialFilterProjectId
}: {
  closePopup?: Function;
  openTaskModal: Function;
  openSnapModal: Function;
  openUserModal: Function;
  setOpenContactUsModal: () => void;
  isSearchScreen?: boolean;
  initialFilterProjectId?: string;
}) {
  //Refs
  const ref = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  //mobx variables
  const { workspaceStore, projectStore, appStore, customerStore } = rootStore;
  const { currentWorkspace } = workspaceStore;
  const { projects } = projectStore;
  const { universalSearchData } = appStore;
  // Mobx variables
  const { customers } = customerStore || {};

  //States
  const [currentTab, setCurrentTab] = useState(TABS_COMMAND_K_ID.ALL);
  const [inputValue, setInputValue] = useState('');
  const [pages, setPages] = useState<string[]>(['home']);
  const [showQuickAction, setShowQuickAction] = useState(true);
  const [showHelp, setShowHelp] = useState(true);
  const [filteredCustomers, setFilteredCustomers] = useState<CustomerDetailsInterface[]>([]);
  const [recentSearch, setRecentSearch] = useState(UserPreferenceSingleton.getInstance().getRecentSearch());
  const [filterProjectId, setFilterProjectId] = useState<string | undefined>(initialFilterProjectId);

  //Other variable
  const activePage = pages[pages.length - 1];
  const isHome = activePage === 'home';
  const history = useHistory();
  const isKeyboardVisible = useKeyboardVisibility();
  const isExtractedView = rootStore.appStore.extractedDesktopView;
  const filterProject = filterProjectId ? projects.find((p) => p.id === filterProjectId) : undefined;

  // Function to store any clicked item in recent searches
  const storeRecentSearch = useCallback(
    (itemName: string, type: number, url: string) => {
      if (!itemName) return;
      const newSearchItem = {
        name: itemName,
        type: type,
        url: url
      };
      const updatedSearches = [
        newSearchItem,
        ...(Array.isArray(recentSearch) ? recentSearch?.filter((e) => e.name !== itemName) : [])
      ]?.slice(0, 2);
      UserPreferenceSingleton.getInstance().setRecentSearch(updatedSearches);
      setRecentSearch(updatedSearches);
    },
    [recentSearch]
  );

  const popPage = React.useCallback(() => {
    setPages((pages) => {
      const x = [...pages];
      x.splice(-1, 1);
      return x;
    });
  }, []);

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        bounce();
      }

      if (isHome || inputValue.length) {
        return;
      }

      if (e.key === 'Backspace') {
        e.preventDefault();
        popPage();
        bounce();
      }
    },
    [inputValue.length, isHome, popPage]
  );

  function bounce() {
    if (ref.current) {
      ref.current.style.transform = 'scale(0.96)';
      setTimeout(() => {
        if (ref.current) {
          ref.current.style.transform = '';
        }
      }, 100);

      setInputValue('');
    }
  }

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const onChangeSearchValue = useCallback(
    (value: string) => {
      if (debounceTimer?.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(() => {
        const searchText = value.trim().toLowerCase();

        const { projects, tasks, users } = convertQueryToWordsCollection(searchText, currentTab, filterProjectId);

        const filterQuickAction: string[] = QUICK_ACTION_LIST.filter((x) =>
          x.trim().toLowerCase().includes(searchText)
        );
        const filterHelp: string[] = HELP_SECTION_LIST.filter((x) => x.trim().toLowerCase().includes(searchText));
        const updatedCustomers = customers.filter(
          (customer) =>
            customer.companyName?.trim().toLowerCase().includes(searchText) ||
            customer.firstName?.trim().toLowerCase().includes(searchText) ||
            customer.lastName?.trim().toLowerCase().includes(searchText)
        );
        setFilteredCustomers(updatedCustomers);
        rootStore.appStore.setUniversalSearchData({ projects, tasks, users });
        setShowQuickAction(filterQuickAction?.length > 0);
        setShowHelp(filterHelp?.length > 0);
      }, 300);
    },
    [currentTab, filterProjectId, customers]
  );

  useEffect(() => {
    onChangeSearchValue(inputValue);
  }, [inputValue, currentTab, filterProjectId, onChangeSearchValue]);

  const onSelectProject = useCallback(
    (project: ProjectModel) => {
      storeRecentSearch(project?.name, 1, `/projects/details/${project?.id}/tasks`);
      rootStore.projectStore.setCurrentProject(project?.id);
      history.push(ROUTES.PROJECT_TASKS.replace(':id', project?.id || ''));
      setInputValue('');
      closePopup?.();
    },
    [storeRecentSearch, history, closePopup]
  );

  const onSelectTask = useCallback(
    (task: TaskModel) => {
      const navigationPath = `/projects/details/${task?.projectId}/tasks/details/${task?.id}`;
      storeRecentSearch(task?.name, 10, navigationPath);
      history.push(navigationPath);
      setInputValue('');
      closePopup?.();
    },
    [storeRecentSearch, history, closePopup]
  );

  const onSelectCustomer = useCallback(
    (customer: CustomerDetailsInterface) => {
      history.push(ROUTES.CUSTOMER_DETAILS.replace(':id', customer.id || ''));
      closePopup?.();
    },
    [history, closePopup]
  );

  const onSelectMember = useCallback(
    (member: UserModel) => {
      storeRecentSearch(member?.name, 2, '');
      rootStore.appStore.setUserModalDetail(member);
      setInputValue('');
      closePopup?.();
      openUserModal();
    },
    [storeRecentSearch, closePopup, openUserModal]
  );

  const onclickHelp = useCallback(() => {
    const newTab = window.open(`https://www.teamcamp.app/help`, '_blank');
    if (newTab) {
      newTab.focus();
      closePopup?.();
    } else {
      alert('Please allow pop-ups for this site to open in a new tab.');
    }
  }, [closePopup]);

  const handleContactSupport = useCallback(() => {
    // Open contact us modal
    if (window.Intercom && typeof window.Intercom === 'function') {
      window.Intercom('show');
    }
    // setOpenContactUsModal();
    closePopup?.();
  }, [closePopup]);

  const onClickCreateProject = useCallback(() => {
    storeRecentSearch('Create new project', 4, '');
    setInputValue('');
    history.push(ROUTES.CREATE_PROJECT);
  }, [history, storeRecentSearch]);

  const onClickSnap = useCallback(() => {
    storeRecentSearch('Snap', 5, '/snap');
    history.push(ROUTES.SNAP);
    setInputValue('');
    closePopup?.();
  }, [history, closePopup, storeRecentSearch]);

  const onClickRecurringTask = useCallback(() => {
    storeRecentSearch('Recurring Task', 7, '/recurring-tasks');
    history.push(ROUTES.RECURRINGTASK);
    setInputValue('');
    closePopup?.();
  }, [history, closePopup, storeRecentSearch]);

  const onClickView = useCallback(() => {
    history.push(ROUTES.VIEWS);
  }, [history]);

  const onClickQuickWorkspaceSwitch = useCallback(async () => {
    const currentDesktopView = await UserPreferenceSingleton.getInstance().getExtractedDesktopView();
    const nextDesktopView = currentDesktopView === DESKTOP_VIEW.OPEN ? DESKTOP_VIEW.CLOSE : DESKTOP_VIEW.OPEN;
    rootStore.appStore.setExtractedDesktopView(nextDesktopView);
    UserPreferenceSingleton.getInstance().setExtractedDesktopView(nextDesktopView);

    setInputValue('');
    closePopup?.();
  }, [closePopup]);

  const onClickCreateTask = useCallback(() => {
    storeRecentSearch('Create new task', 3, '');
    openTaskModal();
    setInputValue('');
    closePopup?.();
  }, [openTaskModal, closePopup, storeRecentSearch]);

  const onClickNavigateSetting = useCallback(() => {
    storeRecentSearch('Navigating to setting', 6, '/settings/personal');
    history.push(ROUTES.PERSONAL_SETTING);
    setInputValue('');
    closePopup?.();
  }, [history, closePopup, storeRecentSearch]);

  const scrollToUp = () => {
    messagesEndRef.current?.scrollIntoView({
      inline: 'nearest'
    });
  };

  useEffect(() => {
    if (!isSearchScreen) scrollToUp();
  }, [inputValue, isSearchScreen]);

  const handleRecentSearchClick = useCallback(
    (searchItem: { name: string; type: number; url: string }) => {
      switch (searchItem.type) {
        case 1: // Project
          history.push(searchItem?.url);
          setInputValue('');
          closePopup?.();
          break;
        case 2: // Member
          const selectedMember = currentWorkspace?.users?.find((user) => user.name === searchItem.name);
          if (selectedMember) {
            onSelectMember(selectedMember);
          }
          break;
        case 3: // Create Task
          onClickCreateTask();
          break;
        case 4: // Create Project
          onClickCreateProject();
          break;
        case 5: // Snap
          onClickSnap();
          break;
        case 6: // Navigate to setting
          onClickNavigateSetting();
          break;
        case 7: // Recurring Task
          onClickRecurringTask();
          break;
        case 8: // Record Snap
          openSnapModal();
          closePopup?.();
          setInputValue('');
          break;
        case 9: // Quick workspace switch
          onClickQuickWorkspaceSwitch();
          break;
        case 10:
          history.push(searchItem?.url);
          setInputValue('');
          closePopup?.();
          break;
        default:
      }
    },
    [
      history,
      closePopup,
      currentWorkspace?.users,
      onClickCreateTask,
      onClickCreateProject,
      onClickSnap,
      onClickNavigateSetting,
      onClickRecurringTask,
      openSnapModal,
      onClickQuickWorkspaceSwitch,
      onSelectMember
    ]
  );

  const clearRecentSearches = useCallback(() => {
    UserPreferenceSingleton.getInstance().setRecentSearch([]);
    setRecentSearch([]);
  }, []);

  const homeRender = useCallback(() => {
    return (
      <>
        <div ref={!isSearchScreen ? messagesEndRef : null} />
        {Array.isArray(recentSearch) && recentSearch.length > 0 && !inputValue && (
          <Command.Group
            heading={
              <MainDiv>
                <Text>Recent searches</Text>
                <ClearDiv role='button' onClick={clearRecentSearches}>
                  Clear
                </ClearDiv>
              </MainDiv>
            }
            className='recent-searches'>
            {recentSearch.map((searchItem, index) => (
              <CmdItem key={index} onSelect={() => handleRecentSearchClick(searchItem)}>
                <IconDiv>
                  <SVGIcon
                    name='search-icon'
                    width={ICON_SIZE.LG}
                    height={ICON_SIZE.LG}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    fill='var(--text-primary)'
                  />
                </IconDiv>
                {searchItem.name}
              </CmdItem>
            ))}
          </Command.Group>
        )}
        {(currentTab === TABS_COMMAND_K_ID.ALL || currentTab === TABS_COMMAND_K_ID.PROJECTS) &&
          universalSearchData?.projects &&
          universalSearchData.projects.length > 0 && (
            <Command.Group heading='Projects' className='projects-label'>
              <Projects list={universalSearchData.projects} onSelectProject={onSelectProject} />
            </Command.Group>
          )}
        {(currentTab === TABS_COMMAND_K_ID.ALL || currentTab === TABS_COMMAND_K_ID.PEOPLE) &&
          universalSearchData?.users &&
          isArray(universalSearchData.users) &&
          universalSearchData.users.length > 0 && (
            <Command.Group heading='People'>
              <Teams list={universalSearchData.users} onSelectMember={onSelectMember} />
            </Command.Group>
          )}
        {(currentTab === TABS_COMMAND_K_ID.ALL || currentTab === TABS_COMMAND_K_ID.TASKS) && (
          <>
            {universalSearchData?.tasks && universalSearchData.tasks.length > 0 && (
              <Command.Group heading='Tasks' className='tasks-label'>
                <Tasks list={universalSearchData.tasks} onSelectTask={onSelectTask} />
              </Command.Group>
            )}
            {filterProjectId && universalSearchData?.tasks?.length === 0 && rootStore.taskStore.tasks.length > 0 && (
              <Command.Group heading='Tasks' className='tasks-label'>
                <ProjectFilterHint>
                  0 tasks found in this project.{' '}
                  <span className='search-workspace-link' role='button' onClick={() => setFilterProjectId(undefined)}>
                    {rootStore.taskStore.tasks.length} tasks in workspace
                  </span>
                </ProjectFilterHint>
              </Command.Group>
            )}
          </>
        )}
        {(currentTab === TABS_COMMAND_K_ID.ALL || currentTab === TABS_COMMAND_K_ID.CUSTOMERS) &&
          filteredCustomers?.length > 0 &&
          (currentWorkspace?.isAdmin || currentWorkspace?.isOwner) && (
            <Command.Group heading='Customers' className='customers-label'>
              <Customers customersList={filteredCustomers} onSelectCustomer={onSelectCustomer} />
            </Command.Group>
          )}

        {currentTab === TABS_COMMAND_K_ID.ALL && showQuickAction && (
          <Command.Group heading='Quick actions'>
            {(isEmpty(inputValue) ||
              'Create new task'.trim().toLowerCase().includes(inputValue.trim().toLowerCase())) && (
              <CmdItem onSelect={onClickCreateTask}>
                <IconDiv>
                  <SVGIcon
                    name='plus-icon'
                    width={ICON_SIZE.LG}
                    height={ICON_SIZE.LG}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    fill='var(--text-secondary)'
                    className='svg-icon'
                  />
                </IconDiv>
                Create new task
              </CmdItem>
            )}
            {currentWorkspace?.isAdmin &&
              (isEmpty(inputValue) ||
                'Create new project'.trim().toLowerCase().includes(inputValue.trim().toLowerCase())) && (
                <CmdItem onSelect={onClickCreateProject}>
                  <IconDiv>
                    <SVGIcon
                      name='plus-icon'
                      width={ICON_SIZE.LG}
                      height={ICON_SIZE.LG}
                      viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                      fill='var(--text-secondary)'
                      className='svg-icon'
                    />
                  </IconDiv>
                  Create new project
                </CmdItem>
              )}
            {(isEmpty(inputValue) || 'Snap'.trim().toLowerCase().includes(inputValue.trim().toLowerCase())) && (
              <CmdItem onSelect={onClickSnap}>
                <IconDiv>
                  <SVGIcon
                    name='snap-icon'
                    width={ICON_SIZE.LG}
                    height={ICON_SIZE.LG}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    fill='var(--text-primary)'
                  />
                </IconDiv>
                Snap
              </CmdItem>
            )}
            {isMediaRecordingSupported() &&
              (isEmpty(inputValue) || 'Record Snap'.trim().toLowerCase().includes(inputValue.trim().toLowerCase())) && (
                <RecordItem
                  storeRecentSearch={storeRecentSearch}
                  openSnapModal={openSnapModal}
                  closePopup={closePopup}
                  setInputValue={setInputValue}
                />
              )}
            {(isEmpty(inputValue) ||
              'Navigating to setting'.trim().toLowerCase().includes(inputValue.trim().toLowerCase())) && (
              <CmdItem onSelect={onClickNavigateSetting}>
                <IconDiv>
                  <SVGIcon
                    name='navigation-icon'
                    width={ICON_SIZE.LG}
                    height={ICON_SIZE.LG}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    fill='var(--text-secondary)'
                    className='svg-icon'
                  />
                </IconDiv>
                Navigating to setting
              </CmdItem>
            )}
            {(isEmpty(inputValue) ||
              'Recurring Task'.trim().toLowerCase().includes(inputValue.trim().toLowerCase())) && (
              <CmdItem onSelect={onClickRecurringTask}>
                <IconDiv>
                  <SVGIcon
                    name='repeat-icon'
                    width={ICON_SIZE.LG}
                    height={ICON_SIZE.LG}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    fill='var(--text-primary)'
                  />
                </IconDiv>
                Recurring Task
              </CmdItem>
            )}
            {(isEmpty(inputValue) || 'views'.trim().toLowerCase().includes(inputValue.trim().toLowerCase())) && (
              <CmdItem onSelect={onClickView}>
                <IconDiv>
                  <SVGIcon
                    name='navbar-views-icon'
                    width={ICON_SIZE.LG}
                    height={ICON_SIZE.LG}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    fill='var(--text-secondary)'
                    className='svg-icon'
                  />
                </IconDiv>
                Views
              </CmdItem>
            )}
            {isElectronApp() &&
              (isEmpty(inputValue) ||
                'Quick workspace switch'.trim().toLowerCase().includes(inputValue.trim().toLowerCase())) && (
                <CmdItem onSelect={onClickQuickWorkspaceSwitch}>
                  <IconDiv>
                    <SVGIcon
                      name='workspace-switch-icon'
                      width={ICON_SIZE.LG}
                      height={ICON_SIZE.LG}
                      viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                      fill='var(--text-secondary)'
                      className='svg-icon'
                    />
                  </IconDiv>
                  <WorkspaceSwitch>
                    Quick workspace switch
                    <button onClick={onClickQuickWorkspaceSwitch}>
                      {isExtractedView === DESKTOP_VIEW.OPEN ? 'On' : 'Off'}
                    </button>
                  </WorkspaceSwitch>
                </CmdItem>
              )}
          </Command.Group>
        )}
        {currentTab === TABS_COMMAND_K_ID.ALL && showHelp && (
          <Command.Group heading='Help'>
            {(isEmpty(inputValue) || 'Search Docs'.trim().toLowerCase().includes(inputValue.trim().toLowerCase())) && (
              <CmdItem
                onSelect={() => {
                  onclickHelp();
                }}>
                <IconDiv>
                  <SVGIcon
                    name='help-center-icon'
                    width={ICON_SIZE.LG}
                    height={ICON_SIZE.LG}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    fill='var(--text-secondary)'
                    className='svg-icon'
                  />
                </IconDiv>
                Search Docs...
              </CmdItem>
            )}
            {(isEmpty(inputValue) ||
              'Send Feedback'.trim().toLowerCase().includes(inputValue.trim().toLowerCase())) && (
              <CmdItem
                onSelect={() => {
                  handleContactSupport();
                }}>
                <IconDiv>
                  <SVGIcon
                    name='feedback-icon'
                    width={ICON_SIZE.LG}
                    height={ICON_SIZE.LG}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    fill='var(--text-secondary)'
                    className='svg-icon'
                  />
                </IconDiv>
                Send Feedback...
              </CmdItem>
            )}
            {(isEmpty(inputValue) ||
              'Contact Support'.trim().toLowerCase().includes(inputValue.trim().toLowerCase())) && (
              <CmdItem
                onSelect={() => {
                  handleContactSupport();
                }}>
                <IconDiv>
                  <SVGIcon
                    name='mail-icon'
                    width={ICON_SIZE.LG}
                    height={ICON_SIZE.LG}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    fill='var(--text-secondary)'
                    className='svg-icon'
                  />
                </IconDiv>
                Contact Support
              </CmdItem>
            )}
          </Command.Group>
        )}
      </>
    );
  }, [
    currentTab,
    onSelectProject,
    onSelectMember,
    universalSearchData?.projects,
    universalSearchData?.users,
    universalSearchData?.tasks,
    filteredCustomers,
    clearRecentSearches,
    closePopup,
    currentWorkspace?.isAdmin,
    currentWorkspace?.isOwner,
    filterProjectId,
    handleContactSupport,
    handleRecentSearchClick,
    inputValue,
    isExtractedView,
    isSearchScreen,
    onClickCreateProject,
    onClickCreateTask,
    onClickNavigateSetting,
    onClickQuickWorkspaceSwitch,
    onClickRecurringTask,
    onClickSnap,
    onClickView,
    onSelectCustomer,
    onSelectTask,
    onclickHelp,
    openSnapModal,
    recentSearch,
    showHelp,
    showQuickAction,
    storeRecentSearch
  ]);

  return (
    <Main>
      {/* isSearchScreen is used for responsive screen */}
      {isSearchScreen && (
        <SearchArea>
          <input
            type='text'
            placeholder='Search for projects, people, tasks, files...'
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            aria-label='Search for projects, people, tasks, files'
          />
          {inputValue && (
            <IconButton
              iconName='new-close-icon'
              size={ICON_BUTTON_SIZE.EXTRA_SMALL}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={() => setInputValue('')}
              className='close-icon-responsive'
              type='rounded'
            />
          )}
        </SearchArea>
      )}
      <PopUp isSearchScreen={isSearchScreen} isKeyboardVisible={isKeyboardVisible}>
        <div className='vercel'>
          <Command ref={ref} shouldFilter={false} onKeyDown={(e: React.KeyboardEvent) => onKeyDown(e)}>
            {!isSearchScreen && (
              <ModalHeader
                autoFocus={true}
                placeHolder='Search for projects, people, tasks, files...'
                searchValue={inputValue}
                onSearchChange={setInputValue}
                onClose={() => closePopup?.()}
              />
            )}
            <Tab>
              {Object.entries(TABS_COMMAND_K).map(([key, value]) =>
                Number(key) === TABS_COMMAND_K_ID.CUSTOMERS &&
                (!customers?.length || !(currentWorkspace?.isAdmin || currentWorkspace?.isOwner)) ? null : (
                  <Tabs
                    key={key}
                    variant={TAB_BUTTON_TYPE.PILL}
                    title={value}
                    isActive={Number(key) === currentTab}
                    onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                      event.stopPropagation();
                      setCurrentTab(Number(key));
                    }}
                  />
                )
              )}
              {filterProject && (
                <ProjectFilterChip>
                  {filterProject.name}
                  <span className='chip-close' role='button' onClick={() => setFilterProjectId(undefined)}>
                    <SVGIcon
                      name='new-close-icon'
                      width={ICON_SIZE.XS}
                      height={ICON_SIZE.XS}
                      viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
                      fill='var(--text-secondary)'
                    />
                  </span>
                </ProjectFilterChip>
              )}
            </Tab>

            <Command.List>
              <Command.Empty>
                <EmptyStateDiv>
                  <EmptyStateV2 description='No result found' size={EMPTY_STATE_SIZE.SMALL} />
                </EmptyStateDiv>
              </Command.Empty>
              {homeRender()}
            </Command.List>
          </Command>
        </div>
      </PopUp>
    </Main>
  );
});

interface Highlight {
  index: number;
  length: number;
}

const renderHighlightedText = (text: string, highlights: Highlight[]) => {
  if (!highlights?.length) return text;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const { index, length } of highlights) {
    if (index > lastIndex) {
      parts.push(text.slice(lastIndex, index));
    }
    parts.push(<Mark key={index}>{text.slice(index, index + length)}</Mark>);
    lastIndex = index + length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};

const Projects = React.memo(function Projects({
  onSelectProject,
  list
}: {
  onSelectProject: (project: ProjectModel) => void;
  list: SearchResultItem<'project'>[];
}) {
  const mobile = useMobileDevice();
  return (
    <>
      {list?.map((project) => (
        <CmdItem key={project?.item?.id} onSelect={() => onSelectProject(project?.item)} value={project?.item?.id}>
          <ProjectIconAvatar
            size={mobile ? 24 : 18}
            type={project?.item?.icon?.type ?? PROJECT_ICON_TYPE.DEFAULT}
            reference={project?.item?.icon?.reference || ''}
            backgroundColor={ICON_COLORS[project?.item?.icon?.color || 1]}
            projectName={project?.item?.name}
            borderRadius={mobile ? '6px' : '4px'}
            iconSize={mobile ? 16 : 13}
            isClickable={false}
          />
          <ProjectName>{renderHighlightedText(project?.item?.name, project.highlights)}</ProjectName>
        </CmdItem>
      ))}
    </>
  );
});

const Tasks = React.memo(function Tasks({
  onSelectTask,
  list
}: {
  onSelectTask: (task: TaskModel) => void;
  list: SearchResultItem<'task'>[];
}) {
  return (
    <>
      {list?.map((task) => (
        <CmdItem
          key={task?.item?.id}
          onSelect={() => onSelectTask(task?.item)}
          value={task?.item?.id}
          className='task-status-icon'>
          <IconDiv className='task-status-icon '>
            <SVGIcon
              name={renderStatusIconName(task?.item?.statusDetails?.Type || STATUS_TYPE.UNSTARTED)}
              width={ICON_SIZE.LG}
              height={ICON_SIZE.LG}
              viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
              fill='var(--text-secondary)'
            />
          </IconDiv>
          <ItemDiv>
            <p> {renderHighlightedText(task?.item?.name, task.highlights)}</p>
            <span className='project-name'>{task?.item?.project?.name}</span>
          </ItemDiv>
        </CmdItem>
      ))}
    </>
  );
});

const Teams = React.memo(function Teams({
  onSelectMember,
  list
}: {
  onSelectMember: (member: UserModel) => void;
  list: SearchResultItem<'user'>[];
}) {
  const mobile = useMobileDevice();
  return (
    <>
      {list?.map((member) => (
        <CmdItem key={member?.item?.id} onSelect={() => onSelectMember(member?.item)}>
          <Avatar
            imgSrc={member?.item?.profile_photo || ''}
            name={member?.item?.name ? member?.item?.name : 'U N'}
            size={mobile ? 24 : 18}
            borderVariant={AVATAR_BORDER.NONE}
            className='search-profile'
          />
          <span>{renderHighlightedText(member?.item?.name, member.highlights)}</span>
        </CmdItem>
      ))}
    </>
  );
});
interface CustomersProps {
  onSelectCustomer: (customer: CustomerDetailsInterface) => void;
  customersList: CustomerDetailsInterface[];
}

const Customers = React.memo(function Customers({ onSelectCustomer, customersList }: CustomersProps) {
  const isMobile = useMobileDevice();

  return (
    <>
      {customersList?.map((customer, index) => (
        <CmdItem key={index} onSelect={() => onSelectCustomer(customer)}>
          <Profile>
            <Image
              imageUrl={customer?.profileImage ? customer?.profileImage : 'empty_profile'}
              alt={customer?.profileImage ? 'profile' : 'empty profile'}
              width={isMobile ? 24 : 18}
              height={isMobile ? 24 : 18}
            />
          </Profile>
          {customer.companyName || `${customer.firstName} ${customer.lastName}`}
        </CmdItem>
      ))}
    </>
  );
});
